import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { breakpoints } from 'Styles/breakpoints';
import { white } from 'Styles/colors';
import { client, generateWidth } from '../../util/cloudinary';
import * as Styled from './ResponsiveImage.styled';

const SORT_ORDER = {
  small: 4,
  medium: 3,
  large: 2,
  xlarge: 1
};

/**
 * `ResponsiveImage` progressively loads images through Cloudinary.
 * It builds a srcset of image sources of different resolutions for specific breakpoints. Resize the window to see the image resize!
 */
const ResponsiveImage = ({
  className,
  cropType,
  src,
  alt,
  resolution,
  background,
  crop,
  isLazy,
  withProgressiveLoading,
  onLowResLoad,
  fetchFormat,
  children,
  ...props
}) => {
  const ConditionalLazyWrapper = isLazy ? Styled.LazyLoad : React.Fragment;
  const [hasHighResImageLoaded, setHighResImageLoaded] = useState(false);
  const responsiveAttrsArray = Object.keys(resolution)
    .sort((a, b) => SORT_ORDER[a] - SORT_ORDER[b])
    .reduce(
      (acc, curr) => {
        if (!breakpoints[curr]) return acc;

        const media = `(min-width: ${breakpoints[curr]}px) ${resolution[curr]}px`;
        const url = client.url(src, {
          type: 'fetch',
          fetch_format: fetchFormat,
          quality: 'auto',
          transformation: [
            { crop: cropType, ...crop },
            {
              background,
              width: resolution[curr],
              crop: 'scale'
            }
          ]
        });
        const image = `${url} ${resolution[curr]}w`;

        return {
          ...acc,
          srcSet: [...acc.srcSet, image],
          sizes: [...acc.sizes, media]
        };
      },
      {
        srcSet: [],
        sizes: []
      }
    );

  return (
    <Styled.Container
      className={className}
      aspectRatio={crop.height && crop.width ? crop.height / crop.width : 1}
      hasLoaded={hasHighResImageLoaded}
    >
      <ConditionalLazyWrapper>
        {/* Quickfix to show only blank placeholder when no image source is provided. */}
        {withProgressiveLoading && src && (
          <Styled.Image
            src={client.url(src, {
              type: 'fetch',
              fetch_format: fetchFormat,
              quality: '1',
              transformation: [
                { crop: cropType, ...crop },
                {
                  background,
                  width: generateWidth(crop),
                  crop: 'scale'
                }
              ]
            })}
            hasLoaded
            onLoad={onLowResLoad}
            alt={alt}
            role="presentation"
          />
        )}
        <Styled.Image
          src={client.url(src, {
            type: 'fetch',
            fetch_format: fetchFormat,
            quality: 'auto',
            transformation: [
              { crop: cropType, ...crop },
              {
                background,
                width: '50',
                crop: 'scale',
                dpr: 'auto'
              }
            ]
          })}
          alt={alt}
          onLoad={() => setHighResImageLoaded(true)}
          hasLoaded={!withProgressiveLoading || hasHighResImageLoaded}
          srcSet={
            responsiveAttrsArray.srcSet.length
              ? responsiveAttrsArray.srcSet.join(', ')
              : null
          }
          sizes={responsiveAttrsArray.sizes.join(', ')}
          {...props}
        />
        {children}
      </ConditionalLazyWrapper>
    </Styled.Container>
  );
};

ResponsiveImage.propTypes = {
  children: PropTypes.node,
  /**
   * CSS Classname
   */
  className: PropTypes.string,
  /**
   * The type of crop
   *
   * @see @link {https://cloudinary.com/documentation/image_transformations#resizing_and_cropping_images}
   */
  cropType: PropTypes.oneOf([
    'scale',
    'fit',
    'limit',
    'mfit',
    'pad',
    'lpad',
    'mpad',
    'crop'
  ]),
  /**
   * remote image url
   */
  src: PropTypes.string,
  /**
   * alternate text for `img` tag
   */
  alt: PropTypes.string,
  /**
   * map of image resolution to render indexed by breakpoint
   */
  resolution: PropTypes.shape({
    small: PropTypes.number,
    medium: PropTypes.number,
    large: PropTypes.number,
    xlarge: PropTypes.number
  }),
  /**
   * background color of the image
   */
  background: PropTypes.string,
  /**
   * optional crop applied before any other image transformations
   */
  crop: PropTypes.shape({
    height: PropTypes.number,
    width: PropTypes.number
  }),
  /**
   * If `true`, image is lazily loaded.
   * @default true
   */
  isLazy: PropTypes.bool,
  /**
   * If 'true', image will be progressively loaded.
   * if 'false', high quality image will be fetched and shown immediately.
   *
   * Should only be set to 'false' for high priority resources such as LCP elements.
   *
   * @default true
   */
  withProgressiveLoading: PropTypes.bool,
  /**
   * A function that is called when the low resolution image has loaded.
   * @param {SyntheticBaseEvent} event
   */
  onLowResLoad: PropTypes.func,
  /**
   * Sends the optimal image file format to the browser
   *
   * Pass null if this should be turned off
   *
   * @see {@link https://cloudinary.com/blog/adaptive_browser_based_image_format_delivery}
   *
   * @default 'auto'
   *
   */
  fetchFormat: PropTypes.oneOf(['auto', null])
};

ResponsiveImage.defaultProps = {
  className: null,
  cropType: 'crop',
  resolution: {},
  alt: null,
  background: white,
  crop: {},
  isLazy: true,
  withProgressiveLoading: true,
  onLowResLoad: null,
  fetchFormat: 'auto',
  src: null
};

export default ResponsiveImage;
